// packages/chatbot/src/components/SystemMessageBubble.tsx

import React, { useMemo } from 'react';
import { Card, IconButton } from '@dopomogai/ui';
import { Info, AlertTriangle, AlertCircle, Copy } from 'lucide-react';

type Severity = 'info' | 'warning' | 'error';

export interface SystemMessageBubbleProps {
  // Supports both 'system_log' and 'page_context'-like messages
  message: {
    id: string;
    type: 'system_log' | 'page_context';
    content: string | Record<string, any>;
    severity?: Severity; // system_log only
    timestamp?: string;
    isExpanded?: boolean; // optional for page_context
  };
}

export const SystemMessageBubble: React.FC<SystemMessageBubbleProps> = ({ message }) => {
  const { content, severity = 'info' } = message;

  const isJson = typeof content === 'object';
  const contentString = isJson ? JSON.stringify(content, null, 2) : String(content);

  const { headerLabel, colorClasses, Icon } = useMemo(() => {
    const forType = message.type === 'page_context' ? 'context' : 'system';
    if (forType === 'context') {
      return {
        headerLabel: 'Context',
        colorClasses: 'text-sky-900 bg-sky-50 border-sky-400',
        Icon: Info,
      };
    }
    switch (severity) {
      case 'warning':
        return {
          headerLabel: 'System',
          colorClasses: 'text-amber-900 bg-amber-50 border-amber-400',
          Icon: AlertTriangle,
        };
      case 'error':
        return {
          headerLabel: 'System',
          colorClasses: 'text-red-900 bg-red-50 border-red-400',
          Icon: AlertCircle,
        };
      default:
        return {
          headerLabel: 'System',
          colorClasses: 'text-slate-900 bg-slate-100 border-slate-300',
          Icon: Info,
        };
    }
  }, [message.type, severity]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(contentString);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex gap-3 w-full justify-start">
      <div className="flex flex-col gap-1 max-w-[85%] items-start w-full">
        <Card className={`w-full border-2 shadow-sm cursor-default select-text ${colorClasses}`} noPadding>
          <header className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4" />
              <span className="font-semibold text-sm">{headerLabel}</span>
            </div>
            <div className="flex items-center gap-1">
              <IconButton
                icon={<Copy className="w-3.5 h-3.5" />}
                onClick={handleCopy}
                ariaLabel="Copy message"
                variant="ghost"
                size="sm"
              />
            </div>
          </header>

          <details className="px-3 pb-3 text-xs text-slate-700 cursor-pointer" open={message.type === 'page_context' ? Boolean(message.isExpanded) : false}>
            <summary className="select-none outline-none">View details</summary>
            <pre className="mt-2 p-2 bg-white border border-slate-200 rounded overflow-auto max-h-64 text-[11px] leading-5">
{contentString}
            </pre>
          </details>
        </Card>
      </div>
    </div>
  );
};

export default SystemMessageBubble;
